import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Grid,
  Card,
  Box,
  Autocomplete,
  IconButton,
  Typography,
} from "@mui/material";
import axios from "../../api";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import AWS from "aws-sdk";
import { useDropzone } from "react-dropzone";
import DeleteIcon from "@mui/icons-material/Delete";
import DateFormat from "examples/ReuseFunctions/DateFormat";
import StatusHistory from "examples/ReuseFunctions/StatusHistory";
import { RootState } from "store";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  asset_type_id: Yup.string().required("Asset Type is required"),
  asset_id: Yup.string().required("Asset is required"),
  description: Yup.string().required("Description is required"),
  address_id: Yup.string().required("Address is required"),
  title: Yup.string().required("Title is required"),
  category_id:Yup.string().required("Category is required"),
});

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});

const initialValues = {
  description: "",
  address_id: "",
  file_name: "",
  file_type: "",
  asset_type_id: "",
  asset_id: "",
  division_id: "",
  title: "",
  category_id: "",
  status_tracker: "",
  serial_number: "",
};

interface UploadedFile {
  name: string;
  type: string;
  size: number;
  preview: string;
  id: string;
}

interface Address {
  state_name: string;
  city_name: string;
  region_name: string;
  address_type: string;
  address: string;
  address_id: number;
}

interface Category {
  category_id: string;
  name: string;
}

interface Asset {
  asset_id: string;
  asset_name: string;
  asset_type_id?: string;
  asset_type_name?: string;
  division_id?: number | null;
  division_name?: string | null;
  address_id?: number | string | null;
  address?: string;
  address_type?: string;
  city_name?: string;
  region_name?: string;
  state_name?: string;
  asset_sku?: string;
}

interface AssetTypes {
  asset_type_id: string;
  asset_type_name: string;
}

interface Division {
  division_id: number;
  division_name: string;
}

interface MediaData {
  file_name: string;
  file_type: string;
  file_path: string;
}

export interface AddTicketProps {
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}
/** Normalize a raw asset API response into the typed Asset shape. */
const normalizeAsset = (asset: any): Asset => ({
  asset_id: String(asset.asset_id),
  asset_name: asset.asset_name,
  asset_type_id: asset.asset_type_id ? String(asset.asset_type_id) : undefined,
  asset_type_name: asset.asset_type_name,
  division_id: asset.division_id ?? null,
  division_name: asset.division_name ?? null,
  address_id: asset.address_id ?? null,
  address: asset.address,
  address_type: asset.address_type,
  city_name: asset.city_name,
  region_name: asset.region_name,
  state_name: asset.state_name,
  asset_sku: asset.asset_sku,
});

/** Resolve the asset-type option from the known list, or build one from the asset data. */
const resolveAssetTypeOption = (
  normalizedAsset: Asset,
  assetsData: AssetTypes[]
): AssetTypes | null =>
  assetsData?.find(
    (option) =>
      String(option.asset_type_id) === String(normalizedAsset.asset_type_id)
  ) ??
  (normalizedAsset.asset_type_id && normalizedAsset.asset_type_name
    ? {
        asset_type_id: normalizedAsset.asset_type_id,
        asset_type_name: normalizedAsset.asset_type_name ?? "",
      }
    : null);

/** Apply asset-type selection side-effects. */
const applyAssetTypeOption = (
  assetTypeOption: AssetTypes | null,
  setSelectedAssetId: (v: AssetTypes | null) => void,
  setFieldValue: (field: string, value: unknown) => void
) => {
  if (!assetTypeOption) return;
  setSelectedAssetId(assetTypeOption);
  setFieldValue("asset_type_id", assetTypeOption.asset_type_id);
};

/** Apply address selection side-effects. */
const applyAddressOption = (
  normalizedAsset: Asset,
  setSelectedAddress: (v: Address | null) => void,
  setAddressData: React.Dispatch<React.SetStateAction<Address[]>>,
  setFieldValue: (field: string, value: unknown) => void
) => {
  if (!normalizedAsset.address_id) {
    setSelectedAddress(null);
    setFieldValue("address_id", "");
    return;
  }
  const normalizedAddress: Address = {
    address: normalizedAsset.address ?? "",
    address_id: Number(normalizedAsset.address_id),
    address_type: normalizedAsset.address_type ?? "",
    city_name: normalizedAsset.city_name ?? "",
    region_name: normalizedAsset.region_name ?? "",
    state_name: normalizedAsset.state_name ?? "",
  };
  setSelectedAddress(normalizedAddress);
  setFieldValue("address_id", String(normalizedAddress.address_id));
  setAddressData((prev) => {
    const exists = prev.some(
      (addr) => String(addr.address_id) === String(normalizedAddress.address_id)
    );
    return exists ? prev : [...prev, normalizedAddress];
  });
};

/** Apply division selection side-effects (company users only). */
const applyDivisionOption = (
  normalizedAsset: Asset,
  setSelectedDivisions: (v: Division | null) => void,
  setDivisions: React.Dispatch<React.SetStateAction<Division[]>>,
  setFieldValue: (field: string, value: unknown) => void
) => {
  if (!normalizedAsset.division_id) {
    setSelectedDivisions(null);
    setFieldValue("division_id", "");
    return;
  }
  const divisionOption: Division = {
    division_id: Number(normalizedAsset.division_id),
    division_name: normalizedAsset.division_name ?? "",
  };
  setSelectedDivisions(divisionOption);
  setFieldValue("division_id", divisionOption.division_id);
  setDivisions((prev) => {
    const exists = prev.some((div) => div.division_id === divisionOption.division_id);
    return exists ? prev : [...prev, divisionOption];
  });
};


const AddTicket = ({
  embedded,
  onSuccess,
  onCancel,
}: AddTicketProps = {}) => {
  const currentTime = DateFormat();
  const [selectedAssetId, setSelectedAssetId] = useState<AssetTypes | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [initialCategory, setInitialCategory] = useState<Category | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<Asset | null>(null);
  const [selectedDivisions, setSelectedDivisions] = useState<Division | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [addressData, setAddressData] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const { fetchError, fetchSuccess } = useSnackbar();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [assetsData, setAssetsData] = React.useState<AssetTypes[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [divisionAssets, setDivisionAssets] = useState<Asset[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [serialLookupLoading, setSerialLookupLoading] = useState(false);
  const [serialLookupError, setSerialLookupError] = useState<string | null>(null);
  const formikApiRef = React.useRef<{
    setFieldValue: (field: string, value: unknown) => void;
  } | null>(null);

  useEffect(() => {
    if (!user?.userId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`/assets/customer/${user.userId}`);
        const list: Asset[] = res?.data?.list ?? [];
        if (cancelled || list.length !== 1) return;
        const asset = list[0];
        const at = assetsData?.find(
          (a) => String(a.asset_type_id) === String(asset.asset_type_id)
        ) ?? (asset.asset_type_id && asset.asset_type_name
          ? {
              asset_type_id: String(asset.asset_type_id),
              asset_type_name: asset.asset_type_name ?? "",
            }
          : null);
        if (!at) return;
        const setFieldValue = formikApiRef.current?.setFieldValue;
        if (setFieldValue) {
          setFieldValue("asset_type_id", at.asset_type_id);
          setFieldValue("asset_id", asset.asset_id);
          if (user?.type === "company" && asset.division_id != null) {
            setFieldValue("division_id", asset.division_id ?? "");
            setSelectedDivisions({
              division_id: asset.division_id,
              division_name: asset.division_name ?? "",
            });
          }
        }
        setSelectedAssetId(at);
        setSelectedAssets(asset);
      } catch (e) {
        console.error("Error fetching customer assets for auto-fill", e);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user?.userId, user?.type, assetsData]);

  const uploadFileToS3 = async (file: UploadedFile): Promise<MediaData> => {
    const fileName = `${Date.now()}-${file.name}`;
    const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME;

    if (!bucketName) {
      throw new Error("AWS bucket configuration is missing");
    }

    const params: AWS.S3.PutObjectRequest = {
      Bucket: bucketName,
      Key: fileName,
      Body: file as any,
      ContentType: file.type,
      ACL: "public-read",
    };

    let type = file.type.split("/")[0];
    if (type === "image") {
      type = "Photo";
    }

    const mediaData: MediaData = {
      file_name: fileName,
      file_type: type,
      file_path: `https://${process.env.REACT_APP_AWS_BUCKET_NAME}.${process.env.REACT_APP_AWS_REGION}.digitaloceanspaces.com/${fileName}`,
    };

    return new Promise((resolve, reject) => {
      s3.upload(params, (err) => {
        if (err) {
          console.error("Error uploading file:", err);
          reject(new Error(`Failed to upload file: ${err.message}`));
        } else {
          resolve(mediaData);
        }
      });
    });
  };

  const uploadAllFiles = async (files: UploadedFile[]): Promise<MediaData[]> => {
    return Promise.all(files.map(uploadFileToS3));
  };

  const createTicketData = (values: typeof initialValues) => {
    const statusdata = StatusHistory(null, user?.name ?? "", "Open");

    const statusTracker = [
      {
        Date: currentTime,
        updatedDate: currentTime,
        status: "open",
        status_id: 0,
        changedBy: user?.name,
        message: "Ticket created",
      },
    ];

    return {
      description: values.description,
      address_id: values.address_id,
      customer_id: user?.userId,
      status_id: 1,
      status_history: statusdata,
      asset_id: values.asset_id,
      division_id: values.division_id === "" ? user?.division_id ?? null : values.division_id ?? user?.division_id ?? null,
      asset_type_id: values.asset_type_id,
      category_id: values.category_id,
      title: values.title,
      status_tracker: JSON.stringify(statusTracker),
    };
  };

  const submitTicketWithoutFiles = async (ticketData: any) => {
    const response = await axios.post("/tickets", { ticketData });
    fetchSuccess(response.data.message);
    if (embedded && onSuccess) {
      onSuccess();
    } else {
      navigate("/customer/tickets-list");
    }
  };

  const submitTicketWithFiles = async (ticketData: any, mediaDataArray: MediaData[]) => {
    const response = await axios.post("/tickets", {
      ticketData,
      mediaData: mediaDataArray,
    });
    fetchSuccess(response.data.message);
    if (embedded && onSuccess) {
      onSuccess();
    } else {
      navigate("/customer/tickets-list");
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setSubmitting(true);
    try {
      const ticketData = createTicketData(values);
      if (uploadedFiles.length > 0) {
        const mediaDataArray = await uploadAllFiles(uploadedFiles);
        await submitTicketWithFiles(ticketData, mediaDataArray);
      } else {
        await submitTicketWithoutFiles(ticketData);
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      } else if (error instanceof Error) {
        fetchError(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fetchAssetsType = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", {
        params: { is_active: true },
      });
      const assetData =
        response.data.list && Array.isArray(response.data.list)
          ? response.data.list
          : [];
      setAssetsData(assetData);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [fetchError]);

  useEffect(() => {
    fetchAssetsType();
  }, [fetchAssetsType]);

  const fetchAddressData = useCallback(async () => {
    try {
      const response = await axios.get(`/address/customer/${user?.userId}`);
      const { data } = response;
      setAddressData(data?.list);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [user, fetchError]);

  useEffect(() => {
    fetchAddressData();
  }, [user, fetchAddressData]);

  const fetchAssets = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `/assets/${selectedAssetId?.asset_type_id}/${user?.userId}`
      );
      setAssets(response?.data?.list);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [selectedAssetId, user?.userId]);

  React.useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const customerIdForDivisions = user?.userId ?? (user as { customer_id?: number })?.customer_id;

  const fetchDivisions = React.useCallback(async () => {
    if (customerIdForDivisions == null) return;
    try {
      const response = await axios.get(`/divisions/customer/${customerIdForDivisions}`);
      setDivisions(response?.data?.divisions ?? []);
    } catch (error) {
      console.error("Error fetching divisions", error);
      setDivisions([]);
    }
  }, [customerIdForDivisions]);

  React.useEffect(() => {
    if (String(user?.type).toLowerCase() === "company" && customerIdForDivisions != null) {
      fetchDivisions();
    }
  }, [user?.type, customerIdForDivisions, fetchDivisions]);

  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles) => {
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            id: `${Date.now()}-${Math.random()}`, //NOSONAR
          })
        ),
      ]);
    },
  });

  const handleDelete = (fileId: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== fileId)
    );
  };

  const upsertAssetInList = (list: Asset[], incoming: Asset) => {
    const index = list.findIndex(
      (item) => String(item.asset_id) === String(incoming.asset_id)
    );
    if (index === -1) return [...list, incoming];
    const clone = [...list];
    clone[index] = { ...clone[index], ...incoming };
    return clone;
  };

  const syncAssetOption = useCallback(
    (assetOption: Asset) => {
      if (user?.type === "company") {
        setDivisionAssets((prev) => upsertAssetInList(prev, assetOption));
      } else {
        setAssets((prev) => upsertAssetInList(prev, assetOption));
      }
    },
    [user?.type]
  );

  // FIX: Refactored handleSerialLookup to reduce Cognitive Complexity from 28 to below 15.
  // All logic is unchanged — branching extracted into dedicated helper functions above the component:
  //   normalizeAsset, resolveAssetTypeOption, applyAssetTypeOption, applyAddressOption, applyDivisionOption
  const handleSerialLookup = useCallback(
    async (
      serialInput: string,
      setFieldValue: (field: string, value: unknown) => void
    ) => {
      const trimmed = serialInput?.trim();
      if (!trimmed) {
        setSerialLookupError("Enter a serial number to search");
        return;
      }
      setSerialLookupLoading(true);
      setSerialLookupError(null);

      try {
        const response = await axios.get(
          `/assets/serial/${encodeURIComponent(trimmed)}`
        );
        const asset = response?.data?.asset;
        if (!asset) {
          throw new Error("Asset not found");
        }

        const normalizedAsset = normalizeAsset(asset);

        const assetTypeOption = resolveAssetTypeOption(normalizedAsset, assetsData);
        applyAssetTypeOption(assetTypeOption, setSelectedAssetId, setFieldValue);

        setInitialCategory(null);
        setFieldValue("category_id", "");

        syncAssetOption(normalizedAsset);
        setSelectedAssets(normalizedAsset);
        setFieldValue("asset_id", normalizedAsset.asset_id);

        applyAddressOption(normalizedAsset, setSelectedAddress, setAddressData, setFieldValue);

        if (user?.type === "company") {
          applyDivisionOption(normalizedAsset, setSelectedDivisions, setDivisions, setFieldValue);
        }

        setFieldValue("serial_number", asset.asset_sku || trimmed);
        fetchSuccess("Asset details auto-filled from serial number.");
      } catch (error) {
        const message =
          error instanceof AxiosError && error.response?.data?.error
            ? error.response.data.error
            : "Asset not found for that serial number";
        setSerialLookupError(message);
        fetchError(message);
      } finally {
        setSerialLookupLoading(false);
      }
    },
    [assetsData, fetchError, fetchSuccess, syncAssetOption, user?.type]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesResponse = await axios.get(
          `/category/asset-type/${selectedAssetId?.asset_type_id}`,
          { params: { is_active: 1 } }
        );
        setCategories(categoriesResponse.data.list);
      } catch (err) {
        console.error("Error fetching data", err);
      }
    };
    fetchData();
  }, [selectedAssetId]);

  const fetchAssetsByDivisionsAndAssetTypes = useCallback(async () => {
    const divisionId = user?.isDivisionUser
      ? user?.division_id
      : selectedDivisions?.division_id;
    if (user?.userId && selectedAssetId?.asset_type_id && divisionId) {
      try {
        const response = await axios.get(
          `/assets/${divisionId}/${selectedAssetId?.asset_type_id}/${user?.userId}`
        );
        setDivisionAssets(response?.data?.list ?? []);
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    }
  }, [
    user?.userId,
    selectedAssetId?.asset_type_id,
    selectedDivisions?.division_id,
    user?.isDivisionUser,
    user?.division_id,
  ]);

  const divisionId = user?.isDivisionUser
    ? user?.division_id
    : selectedDivisions?.division_id;

  useEffect(() => {
    if (!user?.userId || !selectedAssetId?.asset_type_id) return;
    if (user?.type === "customer") {
      fetchAssets();
    } else if (user?.type === "company" && divisionId) {
      fetchAssetsByDivisionsAndAssetTypes();
    }
  }, [
    user?.userId,
    selectedAssetId?.asset_type_id,
    user?.type,
    divisionId,
    fetchAssets,
    fetchAssetsByDivisionsAndAssetTypes,
  ]);

  const assetOptions = user?.type === "company" ? divisionAssets : assets;

  useEffect(() => {
    if (
      !assetOptions?.length ||
      assetOptions.length !== 1 ||
      !selectedAssetId?.asset_type_id
    )
      return;
    const asset = assetOptions[0];
    const setFieldValue = formikApiRef.current?.setFieldValue;
    if (setFieldValue) {
      setFieldValue("asset_id", asset.asset_id);
      if (asset.address_id) {
        setFieldValue("address_id", String(asset.address_id));
      }
    }
    setSelectedAssets(asset);
    if (asset.address_id) {
      setSelectedAddress({
        address: asset.address ?? "",
        address_id: Number(asset.address_id),
        address_type: asset.address_type ?? "",
        city_name: asset.city_name ?? "",
        region_name: asset.region_name ?? "",
        state_name: asset.state_name ?? "",
      });
    } else {
      setSelectedAddress(null);
    }
  }, [assetOptions, selectedAssetId?.asset_type_id]);

  const content = (
    <Card
      style={{
        padding: embedded ? "16px" : "24px",
        width: "100%",
        borderRadius: "8px",
      }}
    >
      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Ticket
        </SoftTypography>
      )}
      <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values }) => {
            formikApiRef.current = { setFieldValue };
            return (
            <Form
              className="new-ticket-form"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Title
                    </SoftTypography>
                    <Field
                      name="title"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      error={!!errors.title && touched.title}
                      helperText={touched.title && errors.title}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Serial Number (optional)
                    </SoftTypography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        name="serial_number"
                        value={values.serial_number}
                        onChange={(event) => {
                          setFieldValue("serial_number", event.target.value);
                          if (serialLookupError) {
                            setSerialLookupError(null);
                          }
                        }}
                        placeholder="Enter asset serial / SKU"
                        variant="outlined"
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={serialLookupLoading}
                        onClick={() =>
                          handleSerialLookup(values.serial_number, setFieldValue)
                        }
                      >
                        {serialLookupLoading ? "Searching..." : "Fetch"}
                      </Button>
                    </Box>
                    {serialLookupError && (
                      <SoftTypography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {serialLookupError}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Asset Type
                    </SoftTypography>
                    <Autocomplete
                      options={assetsData ?? []}
                      getOptionLabel={(option) => option.asset_type_name}
                      value={selectedAssetId}
                      onChange={(event, value) => {
                        setFieldValue("asset_type_id", value?.asset_type_id ?? "");
                        setSelectedAssetId(value);
                        setInitialCategory(null);
                        setSelectedAssets(null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.asset_type_id && touched.asset_type_id}
                          helperText={touched.asset_type_id && errors.asset_type_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                {String(user?.type).toLowerCase() === "company" && user?.isDivisionUser !== true && (
                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Division
                      </SoftTypography>
                      <Autocomplete
                        options={divisions ?? []}
                        getOptionLabel={(option) => option.division_name}
                        value={selectedDivisions}
                        onChange={(event, value) => {
                          setFieldValue("division_id", value?.division_id ?? "");
                          setSelectedDivisions(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.division_id && touched.division_id}
                            helperText={touched.division_id && errors.division_id}
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Asset
                    </SoftTypography>
                    <Autocomplete
                      options={assetOptions ?? []}
                      getOptionLabel={(option) => option.asset_name}
                      value={selectedAssets}
                      onChange={(event, value) => {
                        setFieldValue("asset_id", value?.asset_id ?? "");
                        setSelectedAssets(value);
                        if (value?.address_id) {
                          setSelectedAddress({
                            address: value.address ?? "",
                            address_id: Number(value.address_id),
                            address_type: value.address_type ?? "",
                            city_name: value.city_name ?? "",
                            region_name: value.region_name ?? "",
                            state_name: value.state_name ?? "",
                          });
                          setFieldValue("address_id", String(value.address_id));
                        } else {
                          setSelectedAddress(null);
                          setFieldValue("address_id", "");
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.asset_id && touched.asset_id}
                          helperText={touched.asset_id && errors.asset_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Category
                    </SoftTypography>
                    <Autocomplete
                      options={categories ?? []}
                      getOptionLabel={(option) => option.name}
                      value={initialCategory}
                      onChange={(event, value) => {
                        setFieldValue("category_id", value?.category_id ?? "");
                        setInitialCategory(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.category_id && touched.category_id}
                          helperText={touched.category_id && errors.category_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Address
                    </SoftTypography>
                    <Autocomplete
                      options={addressData ?? []}
                      getOptionLabel={(option) =>
                        `${option.address},${option.address_type}-${option.region_name}-${option.city_name}-${option.state_name}`
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.address_id === value.address_id
                      }
                      value={selectedAddress}
                      onChange={(event, value) => {
                        setSelectedAddress(value);
                        setFieldValue("address_id", value?.address_id ?? "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.address_id && touched.address_id}
                          helperText={touched.address_id && errors.address_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: "left" }}>
                  <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                    Description
                  </SoftTypography>
                  <Field
                    name="description"
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    error={!!errors.description && touched.description}
                    helperText={touched.description && errors.description}
                    sx={{
                      "& .MuiInputBase-root": { alignItems: "flex-start" },
                      "& .MuiInputBase-input": { textAlign: "left" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  {uploadedFiles.length > 0 && (
                    <div className="upload-media">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className="media-container"
                          style={{ position: "relative" }}
                        >
                          {file.preview &&
                            (file.type.startsWith("image") ? (
                              <img
                                src={file.preview}
                                alt={`Uploaded media: ${file.name}`}
                                className="upload-img"
                              />
                            ) : (
                              <video
                                className="upload-video"
                                controls
                                width="100%"
                                height="140px"
                              >
                                <source src={file.preview} type="video/mp4" />
                                <track
                                  kind="captions"
                                  src=""
                                  label="No captions available"
                                />
                              </video>
                            ))}
                          <IconButton
                            onClick={() => handleDelete(file.id)}
                            style={{ position: "absolute", top: 5, right: 5 }}
                            color="secondary"
                            aria-label={`Delete ${file.name}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  )}
                  <Box
                    sx={{ cursor: "pointer", width: "fit-content" }}
                    {...dropzone.getRootProps()}
                  >
                    <Box>
                      <label htmlFor="image-upload-input">
                        <input
                          {...dropzone.getInputProps({
                            name: "file_name",
                          })}
                        />
                        <Button
                          component="span"
                          variant="contained"
                          color="primary"
                          className="upload-btn"
                        >
                          Upload File
                        </Button>
                      </label>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button
                    className="success-btn"
                    sx={{ position: "relative", minWidth: 100 }}
                    variant="contained"
                    disabled={submitting}
                    type="submit"
                    color="success"
                  >
                    Save
                  </Button>
                  <Button
                    className="cancel-btn"
                    sx={{ position: "relative", minWidth: 100, ml: 2.5 }}
                    variant="contained"
                    color="warning"
                    onClick={() =>
                      embedded && onCancel
                        ? onCancel()
                        : navigate("/customer/tickets-list")
                    }
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Form>
          );
          }}
        </Formik>
      </Card>
  );

  if (embedded) {
    return content;
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
};

export default AddTicket;
